import { useMemo } from "react"
import { motion } from "framer-motion"
import { useTargetRect } from "./onboarding-spotlight"

interface OnboardingTooltipProps {
  targetSelector: string
  title: string
  body: string
  stepIndex: number
  totalSteps: number
  onNext: () => void
  onPrev: () => void
  onSkip: () => void
  active: boolean
}

const TOOLTIP_GAP = 12
const TOOLTIP_WIDTH = 280

type Placement = "top" | "bottom" | "left" | "right"

/** Returns the bounding rect of the explorer root */
const getContainerRect = () => {
  const root = document.getElementById("explore-map-v2-root")
  if (!root) return { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight }
  const r = root.getBoundingClientRect()
  return { x: r.x, y: r.y, width: r.width, height: r.height }
}

export const OnboardingTooltip = ({
  targetSelector,
  title,
  body,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  active,
}: OnboardingTooltipProps) => {
  const rect = useTargetRect(targetSelector, active)

  const { style } = useMemo(() => {
    if (!rect) return { style: {} }

    const container = getContainerRect()

    // Convert viewport-relative rect to container-relative
    const targetTop = rect.top - container.y
    const targetLeft = rect.left - container.x
    const targetBottom = targetTop + rect.height
    const targetRight = targetLeft + rect.width

    const cw = container.width
    const ch = container.height

    // Pick side with most space
    const spaceTop = targetTop
    const spaceBottom = ch - targetBottom
    const spaceLeft = targetLeft
    const spaceRight = cw - targetRight

    const spaces: [Placement, number][] = [
      ["bottom", spaceBottom],
      ["top", spaceTop],
      ["right", spaceRight],
      ["left", spaceLeft],
    ]
    const best = spaces.sort((a, b) => b[1] - a[1])[0][0]

    let top = 0
    let left = 0

    switch (best) {
      case "bottom":
        top = targetBottom + TOOLTIP_GAP
        left = targetLeft + rect.width / 2 - TOOLTIP_WIDTH / 2
        break
      case "top":
        top = targetTop - TOOLTIP_GAP
        left = targetLeft + rect.width / 2 - TOOLTIP_WIDTH / 2
        break
      case "right":
        top = targetTop + rect.height / 2
        left = targetRight + TOOLTIP_GAP
        break
      case "left":
        top = targetTop + rect.height / 2
        left = targetLeft - TOOLTIP_GAP - TOOLTIP_WIDTH
        break
    }

    // Clamp within container
    left = Math.max(8, Math.min(left, cw - TOOLTIP_WIDTH - 8))
    top = Math.max(8, Math.min(top, ch - 200))

    const transformOrigin =
      best === "bottom"
        ? "top center"
        : best === "top"
          ? "bottom center"
          : best === "right"
            ? "center left"
            : "center right"

    return {
      style: {
        position: "absolute" as const,
        top,
        left,
        width: TOOLTIP_WIDTH,
        transformOrigin,
        ...(best === "top" ? { transform: "translateY(-100%)" } : {}),
        ...(best === "left" || best === "right" ? { transform: "translateY(-50%)" } : {}),
      },
    }
  }, [rect])

  if (!active || !rect) return null

  const isLast = stepIndex === totalSteps - 1

  return (
    <motion.div
      key={`tooltip-${stepIndex}`}
      className="z-[46]"
      style={style}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: [0.25, 1, 0.5, 1] }}
    >
      <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-4">
        <h3 className="text-sm font-bold text-gray-800 mb-1">{title}</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-3">{body}</p>

        {/* Step dots */}
        <div className="flex items-center justify-center gap-1.5 mb-3">
          {Array.from({ length: totalSteps }, (_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-200 ${
                i === stepIndex ? "w-1.5 h-1.5 bg-[#1E40AF]" : "w-1.5 h-1.5 bg-gray-300"
              }`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button onClick={onSkip} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
            Passer
          </button>
          <div className="flex gap-2">
            {stepIndex > 0 && (
              <button
                onClick={onPrev}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Précédent
              </button>
            )}
            <button
              onClick={onNext}
              className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: "#1E40AF" }}
            >
              {isLast ? "Terminer" : "Suivant"}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  )
}
